import { Badge, Button, Drawer, Stack } from '@mui/material';
import { Iconify } from '../Iconify';
import type { FC } from 'react';
import React, { useState } from 'react';

import type { ConfigType, FilterDefinition } from './declarations/type';
import { NumberRange } from './commonComponents/NumberRange';
import { DateRange } from './commonComponents/DateRange';
import { DateRangeFilter } from './commonComponents/DateRangeFilter';
import { TextFilter } from './commonComponents/TextFilter';
import { SelectFilter } from './commonComponents/SelectFilter';
import { SelectMenu } from './commonComponents/SelectMenu';
import { isFilterSet } from './filterState';
import { useTranslation } from 'react-i18next';

interface FiltersProps {
  filters: ConfigType<any, any, any>['filters'];
  onChange: (value: any) => void;
}

export const Filters: FC<FiltersProps> = ({ filters, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const activeFilters =
    filters?.activeFilters ?? filters?.items.filter(isFilterSet).length ?? 0;
  return (
    <>
      <Button
        // Stays on the right even when the table has no tab bar.
        sx={{ m: 1, ml: 'auto' }}
        variant="outlined"
        startIcon={
          <Badge
            badgeContent={activeFilters}
            color="secondary"
            max={99}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Iconify
              icon={activeFilters ? 'eva:funnel-fill' : 'eva:funnel-outline'}
              width={20}
            />
          </Badge>
        }
        onClick={() => {
          setOpen(true);
        }}
      >
        {t('common.filter')}
      </Button>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          backdrop: {
            invisible: true,
          },
        }}
      >
        <Stack
          style={{ width: 340 }}
          role="presentation"
          sx={{
            gap: 2,
            p: 2,
          }}
        >
          {filters?.items?.map((filter) => (
            <FilterSwitch
              onChange={onChange}
              key={filter.key}
              filter={filter}
            />
          ))}
        </Stack>
      </Drawer>
    </>
  );
};

interface FilterSwitchProps {
  filter: FilterDefinition<any>;
  onChange: (value: any) => void;
  // Filter pill popover: dates and selects render their inline picker instead
  // of a form field.
  inline?: boolean;
  // Called once a single pick completes the filter, like choosing an option.
  onDone?: () => void;
}

export const FilterSwitch: FC<FilterSwitchProps> = ({
  filter,
  onChange,
  inline,
  onDone,
}) => {
  switch (filter.type) {
    case 'custom':
      return <>{filter.component}</>;
    case 'text':
      return (
        <TextFilter
          label={filter.label}
          value={filter.value}
          inline={filter.inline}
          onChange={(value) => {
            onChange(value);
            filter.onChange(value);
          }}
        />
      );
    case 'select':
      return inline ? (
        <SelectMenu
          options={filter.options}
          value={filter.value}
          onChange={(value) => {
            onChange(value);
            filter.onChange(value);
            onDone?.();
          }}
        />
      ) : (
        <SelectFilter
          label={filter.label}
          options={filter.options}
          value={filter.value}
          onChange={(value) => {
            onChange(value);
            filter.onChange(value);
          }}
        />
      );
    case 'boolean':
      break;
    case 'numberRange':
      return (
        <NumberRange
          onRangeChange={(min, max) => {
            const value = { from: min ?? undefined, to: max ?? undefined };
            onChange(value);
            return filter.onChange(value);
          }}
          minValue={filter.min}
          maxValue={filter.max}
          label={filter.label}
        />
      );
    case 'dateRange': {
      const onRangeChange = (value: { from?: Date; to?: Date }) => {
        onChange(value);
        filter.onChange(value);
      };
      return inline ? (
        <DateRangeFilter range={filter.range} onChange={onRangeChange} />
      ) : (
        <DateRange
          label={filter.label}
          range={filter.range}
          onChange={onRangeChange}
        />
      );
    }
  }

  return null;
};
